<script setup lang="ts">
import type { CuratedEvent, CuratedEventOutcome } from '~/types/curated-events';

const LEAGUE_IDS_WITH_WHITE_BG = new Set<string>([
  '740557258543472640', // Inglaterra - Premier League
  '811554459741384704', // Brazil Cup
  '805808858558951424', // Netherlands - Eredivisie
  '821352502514806784', // Copa Libertadores
  '749985683653128192', // Copa Sudamericana
  '722933187529228288', // Europa League
  '729216865012039680', // Portugal - Primeira Liga
  '725635248888770560', // France - Ligue 1
  '749297647965548544', // Champions League
  '749898766232174592', // Europa Conference League
]);

const TEAM_IDS_WITH_WHITE_BG = new Set<string>([
  '143894', // Maringá FC
  '242', // Tottenham
]);

type Props = {
  event: CuratedEvent;
};

const { event } = defineProps<Props>();
const { goToLocaleRoute } = useRoutes();

const hasWhiteBg = computed(() =>
  LEAGUE_IDS_WITH_WHITE_BG.has(event.league.id),
);

const homeHasWhiteBg = computed(() =>
  event.home ? TEAM_IDS_WITH_WHITE_BG.has(event.home.id) : false,
);

const awayHasWhiteBg = computed(() =>
  event.away ? TEAM_IDS_WITH_WHITE_BG.has(event.away.id) : false,
);

const startDate = computed<Date | null>(() =>
  event.startTime ? new Date(event.startTime) : null,
);

const headerDate = computed<string>(() => {
  if (!startDate.value) return '';

  const day = String(startDate.value.getDate()).padStart(2, '0');
  const month = String(startDate.value.getMonth() + 1).padStart(2, '0');
  const time = startDate.value.toLocaleTimeString('pt-BR', {
    hour: '2-digit',
    minute: '2-digit',
  });

  return `${day}/${month} | ${time}`;
});

const eventSuspended = computed(() => event.status === 'SUSPENDED');
const marketSuspended = computed(
  () => event.matchResult?.status === 'SUSPENDED',
);

const isPast = computed(
  () => !!startDate.value && startDate.value.getTime() < Date.now(),
);

const outcomes = computed(() => {
  const mr = event.matchResult;

  return {
    home: mr?.home ?? null,
    draw: mr?.draw ?? null,
    away: mr?.away ?? null,
  };
});

function isOutcomeAvailable(o: CuratedEventOutcome | null): boolean {
  return Boolean(
    o &&
      o.status === 'ACTIVE' &&
      !marketSuspended.value &&
      !eventSuspended.value &&
      !isPast.value,
  );
}

function formatOdds(value: number): string {
  return value.toFixed(2).replace('.', ',');
}

function goToSelectOdd(outcome: CuratedEventOutcome | null) {
  if (!isOutcomeAvailable(outcome) || !outcome) return;

  goToLocaleRoute({
    name: 'sports-sports',
    query: { eventId: event.eventId, selectionId: outcome.selectionId },
  });
}
</script>

<template>
  <div :class="[$style.card, (eventSuspended || isPast) && $style.suspended]">
    <!-- Header: data em formato de aba centralizada -->
    <div :class="$style.dateTab">
      <svg
        :class="$style.dateTabShape"
        fill="none"
        viewBox="0 0 150 27"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M148.732 0.47168L131.235 22.5654C129.534 24.7139 126.944 25.9668 124.203 25.9668H25.5049C22.7644 25.9667 20.174 24.7138 18.4727 22.5654L0.975586 0.47168H148.732Z"
          stroke="url(#dateTabStroke)"
          stroke-width="0.944252"
        />
        <defs>
          <linearGradient
            id="dateTabStroke"
            gradientUnits="userSpaceOnUse"
            x1="75.3776"
            x2="75.3776"
            y1="29.6117"
            y2="6.87415"
          >
            <stop stop-color="white" />
            <stop
              offset="1"
              stop-color="white"
              stop-opacity="0"
            />
          </linearGradient>
        </defs>
      </svg>
      <span :class="$style.date">{{ headerDate }}</span>
    </div>

    <!-- Body: 3 colunas (mandante / liga / visitante) -->
    <div :class="$style.body">
      <div :class="$style.col">
        <img
          :alt="event.home?.name ?? ''"
          :class="[
            $style.teamLogo,
            homeHasWhiteBg && $style.teamLogoForceWhite,
          ]"
          draggable="false"
          :src="event.home?.logo || '/assets/icons/sports/team-fallback.svg'"
        />
      </div>

      <div :class="[$style.col, $style.colLeague]">
        <img
          :alt="event.league.name"
          :class="[
            $style.leagueLogo,
            !event.league.logo && $style.leagueLogoFallback,
            hasWhiteBg && $style.leagueLogoForceWhite,
          ]"
          draggable="false"
          :src="event.league.logo || '/assets/icons/sports/league-fallback.svg'"
        />
        <span :class="$style.leagueName">{{ event.league.name }}</span>
      </div>

      <div :class="$style.col">
        <img
          :alt="event.away?.name ?? ''"
          :class="[
            $style.teamLogo,
            awayHasWhiteBg && $style.teamLogoForceWhite,
          ]"
          draggable="false"
          :src="event.away?.logo || '/assets/icons/sports/team-fallback.svg'"
        />
      </div>
    </div>

    <!-- Labels acima das odds -->
    <div :class="$style.labels">
      <span :class="$style.label">{{ event.home?.name ?? '—' }}</span>
      <span :class="$style.label">Empate</span>
      <span :class="$style.label">{{ event.away?.name ?? '—' }}</span>
    </div>

    <!-- Odds 1X2 -->
    <div :class="$style.odds">
      <button
        :class="[
          $style.odd,
          !isOutcomeAvailable(outcomes.home) && $style.oddDisabled,
        ]"
        :disabled="!isOutcomeAvailable(outcomes.home)"
        type="button"
        @click="goToSelectOdd(outcomes.home)"
      >
        <span :class="$style.labelInside">{{ event.home?.name ?? '—' }}</span>
        <div :class="$style.oddRow">
          <svg
            :class="$style.bolt"
            fill="currentColor"
            viewBox="0 0 15 19"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 10.6228L1.18031 0H9.44252L7.67204 5.90157H14.1638V7.67204L4.72126 18.885L6.49173 10.6228H0Z"
            />
          </svg>
          <span :class="$style.oddValue">{{
            outcomes.home ? formatOdds(outcomes.home.odds) : '—'
          }}</span>
        </div>
      </button>

      <button
        :class="[
          $style.odd,
          !isOutcomeAvailable(outcomes.draw) && $style.oddDisabled,
        ]"
        :disabled="!isOutcomeAvailable(outcomes.draw)"
        type="button"
        @click="goToSelectOdd(outcomes.draw)"
      >
        <span :class="$style.labelInside">Empate</span>
        <div :class="$style.oddRow">
          <svg
            :class="$style.bolt"
            fill="currentColor"
            viewBox="0 0 15 19"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 10.6228L1.18031 0H9.44252L7.67204 5.90157H14.1638V7.67204L4.72126 18.885L6.49173 10.6228H0Z"
            />
          </svg>
          <span :class="$style.oddValue">{{
            outcomes.draw ? formatOdds(outcomes.draw.odds) : '—'
          }}</span>
        </div>
      </button>

      <button
        :class="[
          $style.odd,
          !isOutcomeAvailable(outcomes.away) && $style.oddDisabled,
        ]"
        :disabled="!isOutcomeAvailable(outcomes.away)"
        type="button"
        @click="goToSelectOdd(outcomes.away)"
      >
        <span :class="$style.labelInside">{{ event.away?.name ?? '—' }}</span>
        <div :class="$style.oddRow">
          <svg
            :class="$style.bolt"
            fill="currentColor"
            viewBox="0 0 15 19"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M0 10.6228L1.18031 0H9.44252L7.67204 5.90157H14.1638V7.67204L4.72126 18.885L6.49173 10.6228H0Z"
            />
          </svg>
          <span :class="$style.oddValue">{{
            outcomes.away ? formatOdds(outcomes.away.odds) : '—'
          }}</span>
        </div>
      </button>
    </div>
  </div>
</template>

<style module lang="postcss">
.card {
  @apply relative flex flex-col w-[82vw] sm:w-[350px] flex-shrink-0;
  padding: 30px 14px 12px;
  gap: 0;
  border-radius: 9.44px;
  background: radial-gradient(
    168.3% 138% at 36.76% -16.67%,
    #1515e5 0%,
    #000098 100%
  );

  &.suspended {
    @apply opacity-60;
  }

  /* Aba decorativa centralizada no topo (SVG path com gradient stroke) */
  .dateTab {
    @apply absolute left-1/2 -translate-x-1/2 flex items-center justify-center pointer-events-none;
    top: 0;
    width: 150px;
    height: 27px;

    .dateTabShape {
      @apply absolute inset-0 w-full h-full;
    }

    .date {
      @apply relative text-white whitespace-nowrap;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: 11px;
      line-height: 1;
    }
  }

  .body {
    @apply grid grid-cols-3 items-center;
    gap: 8px;

    .col {
      @apply flex flex-col items-center justify-center min-w-0;
      height: 84px;

      .teamLogo {
        @apply object-contain;
        width: 56px;
        height: 56px;

        &.teamLogoForceWhite {
          filter: brightness(0) invert(1);
        }
      }
    }

    .colLeague {
      @apply justify-center self-stretch;
      gap: 4px;

      .leagueLogo {
        @apply object-contain;
        width: 54px;
        height: 54px;

        &.leagueLogoForceWhite {
          filter: brightness(0) invert(1);
        }
      }

      .leagueLogoFallback {
        width: 46px;
        height: 46px;
      }

      .leagueName {
        @apply text-white text-center truncate;
        max-width: 140px;
        width: 140px;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: 9px;
        line-height: 1.4;
      }
    }
  }

  .labels {
    @apply hidden lg:grid grid-cols-3;
    gap: 6px;

    .label {
      @apply text-white text-center truncate max-w-full;
      font-family: 'Plus Jakarta Sans', sans-serif;
      font-weight: 700;
      font-size: 10.39px;
      line-height: 1.4;
      padding-block: 1px;
    }
  }

  .odds {
    @apply grid grid-cols-3;
    gap: 6px;

    .odd {
      @apply flex flex-col items-center justify-center self-stretch transition-colors lg:flex-row;
      padding: 3px 4px;
      gap: 2px;
      position: relative;
      border-radius: 6px;
      border: 1px solid #00c9ff;
      background: linear-gradient(
        0deg,
        rgba(0, 201, 255, 0.2) 0%,
        rgba(0, 201, 255, 0) 100%
      );

      @media (min-width: 1024px) {
        @apply bg-transparent;
        height: 36px;
        padding: 0;
        gap: 4.721px;
        border-radius: 5.67px;
        border: none;

        &::before {
          content: '';
          @apply absolute inset-0 pointer-events-none;
          border-radius: inherit;
          padding: 1px;
          background: linear-gradient(
            180deg,
            rgba(255, 255, 255, 0) 0%,
            rgba(255, 255, 255, 1) 100%
          );
          -webkit-mask:
            linear-gradient(#000 0 0) content-box,
            linear-gradient(#000 0 0);
          -webkit-mask-composite: xor;
          mask-composite: exclude;
        }
      }

      &:hover:not(.oddDisabled) {
        background: rgba(255, 255, 255, 0.08);
      }

      &.oddDisabled {
        @apply opacity-50 cursor-not-allowed;
      }

      .labelInside {
        @apply lg:hidden text-white truncate max-w-full text-center;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: 10.39px;
        line-height: 1.4;
        padding-block: 1px;
      }

      .oddRow {
        @apply flex items-center justify-center;
        gap: 4.721px;
      }

      .oddValue {
        @apply text-white;
        font-family: 'Plus Jakarta Sans', sans-serif;
        font-weight: 700;
        font-size: 14px;
        line-height: 1;

        @media (min-width: 1024px) {
          font-size: 18.89px;
        }
      }

      .bolt {
        @apply flex-shrink-0;
        color: #00c9ff;
        width: 12px;
        height: 15px;

        @media (min-width: 1024px) {
          @apply text-white;
          width: 15px;
          height: 19px;
        }
      }
    }
  }
}
</style>
